import types from '../mutation-types';
import KanbanAPI from '../../api/kanban';

const CACHE_DURATION_MS = 60 * 1000; // Cache por 1 minuto

const state = {
  isEnabled: false,
  items: [],
  loading: false,
  error: null,
  config: {
    title: window.globalConfig.KANBAN_TITLE || 'Gestor de Pedidos',
    defaultView: 'kanban',
    webhookUrl: '',
    webhookSecret: '',
    autoAssignment: false,
    notificationsEnabled: false,
    supportEmail: '',
    supportPhone: '',
    supportChatHours: '',
    supportChatEnabled: false,
  },
  lastUpdated: null,
};

const getters = {
  isKanbanEnabled: _state => _state.isEnabled,
  getKanbanItems: _state => _state.items,
  isLoading: _state => _state.loading,
  getError: _state => _state.error,
  getKanbanConfig: _state => _state.config,
  getKanbanTitle: _state =>
    window.globalConfig.KANBAN_TITLE || _state.config?.title || 'Kanban',
  getSupportEmail: _state => _state.config?.supportEmail || '',
  getSupportPhone: _state => _state.config?.supportPhone || '',
  getSupportChatHours: _state => _state.config?.supportChatHours || '',
  getSupportChatEnabled: _state => _state.config?.supportChatEnabled || false,
};

const actions = {
  setKanbanEnabled({ commit }, isEnabled) {
    commit(types.SET_KANBAN_ENABLED, isEnabled);
  },

  async fetchKanbanItems({ commit, state: localState }) {
    console.log('[STORE-DEBUG] fetchKanbanItems iniciado');
    // Verifica se o cache é válido
    const now = new Date().getTime();
    const isCacheValid =
      localState.items.length > 0 &&
      localState.lastUpdated &&
      now - localState.lastUpdated < CACHE_DURATION_MS;

    console.log(`[STORE-DEBUG] Cache válido? ${isCacheValid}, Última Atualização: ${localState.lastUpdated ? new Date(localState.lastUpdated) : 'N/A'}, Agora: ${new Date(now)}`);

    if (isCacheValid) {
      console.log('[STORE-DEBUG] Usando dados do cache.');
      // Retorna os dados cacheados sem mudar o estado de loading
      // ou podemos até remover o loading se o cache for suficiente
      return localState.items;
    }

    console.log('[STORE-DEBUG] Cache inválido ou inexistente, buscando da API.');
    commit('SET_LOADING', true);
    try {
      const currentFunnel = this.getters['funnel/getSelectedFunnel'];
      if (!currentFunnel?.id) {
        throw new Error('Nenhum funil selecionado');
      }

      console.log(
        '[STORE-DEBUG] Buscando itens para o funil:',
        currentFunnel.id
      );
      const response = await KanbanAPI.getItems(currentFunnel.id);
      console.log(
        '[STORE-DEBUG] API getItems respondeu com',
        response.data.length,
        'itens'
      );

      commit('SET_KANBAN_ITEMS', response.data);
      commit('SET_LAST_UPDATED'); // Atualiza o timestamp do último fetch
      console.log(
        '[STORE-DEBUG] fetchKanbanItems - Store atualizado com novos itens:',
        localState.items.length
      );
      console.log(
        '[STORE-DEBUG] fetchKanbanItems - IDs dos novos itens:',
        localState.items.map(i => i.id)
      );
      return response.data;
    } catch (error) {
      console.error('[STORE-DEBUG] Erro em fetchKanbanItems:', error);
      commit('SET_ERROR', error.message);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async createKanbanItem({ commit }, payload) {
    commit('SET_LOADING', true);
    try {
      const response = await KanbanAPI.createItem(payload);
      commit('ADD_KANBAN_ITEM', response.data);
      commit('SET_LAST_UPDATED'); // Invalida cache ao criar
      return response.data;
    } catch (error) {
      commit('SET_ERROR', error.message);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async moveItemToStage({ commit, dispatch }, { itemId, columnId }) {
    console.log(
      '[MOVE-STORE-DEBUG] moveItemToStage iniciado - itemId:',
      itemId,
      'columnId:',
      columnId
    );

    // Garantir que itemId seja número
    const numericItemId = parseInt(itemId, 10);

    // Obter o item original para possível reversão
    const originalItem = state.items.find(i => i.id === numericItemId);
    console.log(
      '[MOVE-STORE-DEBUG] Item encontrado?',
      !!originalItem,
      'ID numérico:',
      numericItemId
    );
    console.log(
      '[MOVE-STORE-DEBUG] IDs no store:',
      state.items.map(i => i.id)
    );

    if (originalItem) {
      console.log(
        '[MOVE-STORE-DEBUG] Item antes da atualização:',
        originalItem.funnel_stage
      );
      // Modificar o item no store antes da API (igual à exclusão)
      commit('UPDATE_ITEM_STAGE', { itemId: numericItemId, columnId });
      console.log(
        '[MOVE-STORE-DEBUG] Após commit, valor do item atualizado:',
        state.items.find(i => i.id === numericItemId)?.funnel_stage
      );
    }

    try {
      // Chamar a API
      await KanbanAPI.moveToStage(numericItemId, columnId);
      // --- FIX: Invalidar cache do frontend após sucesso ---
      await dispatch('itemUpdated');
      // --- END FIX ---
      return true;
    } catch (error) {
      console.error('[MOVE-STORE-DEBUG] Erro na API ao mover item:', error);
      // Em caso de erro, reverter para o estado original
      if (originalItem) {
        commit('UPDATE_ITEM_STAGE', {
          itemId: numericItemId,
          columnId: originalItem.funnel_stage,
        });
      }
      commit('SET_ERROR', error.message);
      throw error;
    }
  },

  async deleteKanbanItem({ commit }, itemId) {
    const originalItem = state.items.find(i => i.id === itemId);
    commit('REMOVE_KANBAN_ITEM', itemId);

    try {
      await KanbanAPI.deleteItem(itemId);
      commit('SET_LAST_UPDATED'); // Invalida cache ao deletar
      return true;
    } catch (error) {
      if (originalItem) {
        commit('ADD_KANBAN_ITEM', originalItem);
      }
      commit('SET_ERROR', error.message);
      throw error;
    }
  },

  async updateItemStructure({ commit, dispatch }, { itemId, structure }) {
    commit('SET_LOADING', true);
    try {
      const response = await KanbanAPI.updateItemStructure(itemId, {
        item: { item_structure: structure },
      });
      commit('UPDATE_ITEM_STRUCTURE', {
        itemId,
        structure: response.data.item_structure,
      });
      await dispatch('itemUpdated'); // Invalida cache
      return response.data;
    } catch (error) {
      commit('SET_ERROR', error.message);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async updateItemDetails({ commit, dispatch }, { itemId, details }) {
    console.log(`[STORE-DEBUG] updateItemDetails iniciado - Item ID: ${itemId}, Detalhes:`, details);
    // --- Otimização: Atualizar o store localmente primeiro para UI mais rápida --- //
    commit('UPDATE_ITEM_DETAILS', { itemId, details });
    console.log(`[STORE-DEBUG] updateItemDetails - Store local atualizado (antes da API)`);

    try {
      console.log(`[STORE-DEBUG] updateItemDetails - Chamando API.getItem para ID: ${itemId}`);
      const currentItem = await KanbanAPI.getItem(itemId);
      console.log(`[STORE-DEBUG] updateItemDetails - Dados atuais da API:`, currentItem.data);
      const payload = {
        funnel_id: details.funnel_id ?? currentItem.data.funnel_id,
        funnel_stage: details.funnel_stage ?? currentItem.data.funnel_stage,
        position: details.position ?? currentItem.data.position,
        item_details: details.item_details ?? currentItem.data.item_details,
        conversation_display_id: details.conversation_display_id ?? currentItem.data.conversation_display_id,
        timer_started_at: details.timer_started_at ?? currentItem.data.timer_started_at,
        timer_duration: details.timer_duration ?? currentItem.data.timer_duration,
        custom_attributes: details.custom_attributes ?? currentItem.data.custom_attributes,
      };
      console.log(`[STORE-DEBUG] updateItemDetails - Payload para API.updateItem:`, payload);
      const response = await KanbanAPI.updateItem(itemId, payload);
      console.log(`[STORE-DEBUG] updateItemDetails - Resposta da API.updateItem:`, response.data);

      // --- Garantir que o store local reflete a resposta da API --- //
      // commit('UPDATE_ITEM_DETAILS', { itemId, details: response.data }); // Descomente se necessário sincronizar com a resposta exata
      // console.log(`[STORE-DEBUG] updateItemDetails - Store local atualizado com resposta da API`);

      await dispatch('itemUpdated'); // Invalida o cache
      console.log(`[STORE-DEBUG] updateItemDetails - Cache invalidado (itemUpdated chamado)`);
      return response.data;
    } catch (error) {
      console.error('[STORE-DEBUG] Erro em updateItemDetails:', error);
      // TODO: Reverter a mutação local em caso de erro?
      commit('SET_ERROR', error.message);
      throw error;
    }
  },

  async registerActivity({ commit, dispatch }, { itemId, type, details }) {
    try {
      const currentItem = await KanbanAPI.getItem(itemId);
      const newActivity = {
        id: Date.now(),
        type,
        details,
        created_at: new Date().toISOString(),
        user: {
          id: this.getters.getCurrentUser.id,
          name: this.getters.getCurrentUser.name,
          avatar_url: this.getters.getCurrentUser.avatar_url,
        },
      };

      const activities = [
        ...(currentItem.data.item_details?.activities || []),
        newActivity,
      ];

      const payload = {
        ...currentItem.data,
        item_details: {
          ...currentItem.data.item_details,
          activities,
        },
      };

      const response = await KanbanAPI.updateItem(itemId, payload);
      await dispatch('itemUpdated');
      return response.data;
    } catch (error) {
      commit('SET_ERROR', error.message);
      throw error;
    }
  },

  async fetchKanbanConfig({ commit }) {
    try {
      const response = await KanbanAPI.fetchConfig();

      const config = {
        title:
          window.globalConfig.KANBAN_TITLE ||
          response.data.KANBAN_TITLE ||
          'Kanban',
        defaultView: response.data.KANBAN_DEFAULT_VIEW || 'kanban',
        webhookUrl: response.data.KANBAN_WEBHOOK_URL || '',
        webhookSecret: response.data.KANBAN_WEBHOOK_SECRET || '',
        autoAssignment: response.data.KANBAN_AUTO_ASSIGNMENT === 'true',
        notificationsEnabled:
          response.data.KANBAN_NOTIFICATION_ENABLED === 'true',
        supportEmail: response.data.KANBAN_SUPPORT_EMAIL || '',
        supportPhone: response.data.KANBAN_SUPPORT_PHONE || '',
        supportChatHours: response.data.KANBAN_SUPPORT_CHAT_HOURS || '',
        supportChatEnabled:
          response.data.KANBAN_SUPPORT_CHAT_ENABLED === 'true',
      };

      commit('SET_KANBAN_CONFIG', config);
      return config;
    } catch (error) {
      console.warn('Erro ao carregar configurações do Kanban:', error);
      const defaultConfig = {
        title: window.globalConfig.KANBAN_TITLE || 'Kanban',
        defaultView: 'kanban',
        webhookUrl: '',
        webhookSecret: '',
        autoAssignment: false,
        notificationsEnabled: false,
        supportEmail: '',
        supportPhone: '',
        supportChatHours: '',
        supportChatEnabled: false,
      };
      commit('SET_KANBAN_CONFIG', defaultConfig);
      return defaultConfig;
    }
  },

  updateLocalItems({ commit }, items) {
    commit('SET_KANBAN_ITEMS', items);
    commit('SET_LAST_UPDATED'); // Atualiza o timestamp ao receber dados externos
  },

  async itemUpdated({ commit }) {
    console.log('[STORE-DEBUG] itemUpdated action chamada - Invalidando cache.');
    commit('SET_LAST_UPDATED');
    return true;
  },
};

const mutations = {
  [types.SET_KANBAN_ENABLED](_state, isEnabled) {
    _state.isEnabled = isEnabled;
  },
  SET_LOADING(_state, loading) {
    _state.loading = loading;
  },
  SET_ERROR(_state, error) {
    _state.error = error;
  },
  SET_KANBAN_ITEMS(_state, items) {
    _state.items = items;
  },
  ADD_KANBAN_ITEM(_state, item) {
    _state.items.push(item);
  },
  REMOVE_KANBAN_ITEM(_state, itemId) {
    _state.items = _state.items.filter(item => item.id !== itemId);
  },
  UPDATE_ITEM_STAGE(_state, { itemId, columnId }) {
    console.log(
      '[MUTATION-DEBUG] UPDATE_ITEM_STAGE chamada - itemId:',
      itemId,
      'columnId:',
      columnId
    );

    // Garantir que o itemId seja um número
    const numericItemId = parseInt(itemId, 10);
    console.log(
      '[MUTATION-DEBUG] Tipo do itemId:',
      typeof itemId,
      'Valor numérico:',
      numericItemId
    );

    // Buscar o item usando o ID numérico
    const item = _state.items.find(i => i.id === numericItemId);
    console.log(
      '[MUTATION-DEBUG] Item encontrado?',
      !!item,
      'IDs no store:',
      _state.items.map(i => i.id)
    );

    // Atualizar o item se encontrado
    if (item) {
      console.log(
        '[MUTATION-DEBUG] Atualizando item:',
        item.id,
        'de',
        item.funnel_stage,
        'para',
        columnId
      );
      item.funnel_stage = columnId;
      console.log('[MUTATION-DEBUG] Após atualização:', item.funnel_stage);
    } else {
      console.log('[MUTATION-DEBUG] Item não encontrado! ID:', numericItemId);
    }
  },
  UPDATE_ITEM_STRUCTURE(_state, { itemId, structure }) {
    const item = _state.items.find(i => i.id === itemId);
    if (item) {
      item.item_structure = structure;
    }
  },
  UPDATE_ITEM_DETAILS(_state, { itemId, details }) {
    console.log(`[STORE-MUTATION-DEBUG] UPDATE_ITEM_DETAILS - Item ID: ${itemId}, Detalhes:`, details);
    const item = _state.items.find(i => i.id === itemId);
    if (item) {
      console.log('[STORE-MUTATION-DEBUG] UPDATE_ITEM_DETAILS - Item encontrado, atualizando...');
      // Atualizar todos os campos relevantes que estão nos 'details'
      // Garantir que apenas as propriedades existentes em 'details' sejam atualizadas
      Object.keys(details).forEach(key => {
        if (key in item) {
          item[key] = details[key];
        } else if (key === 'item_details') {
          item.item_details = {
            ...item.item_details, // Mantém detalhes existentes
            ...details.item_details, // Sobrescreve com novos detalhes
          };
        }
      });
      console.log('[STORE-MUTATION-DEBUG] UPDATE_ITEM_DETAILS - Item após atualização:', item);
    } else {
      console.warn(`[STORE-MUTATION-DEBUG] UPDATE_ITEM_DETAILS - Item com ID ${itemId} não encontrado no store.`);
    }
  },
  SET_KANBAN_CONFIG(_state, config) {
    _state.config = config;
  },
  SET_LAST_UPDATED(_state) {
    const oldTimestamp = _state.lastUpdated;
    _state.lastUpdated = new Date().getTime();
    console.log(`[STORE-MUTATION-DEBUG] SET_LAST_UPDATED - Timestamp atualizado de ${oldTimestamp ? new Date(oldTimestamp) : 'N/A'} para ${new Date(_state.lastUpdated)}`);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
